import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { RootState } from '../../app/store';
import type { CameraControlHandle } from '../../components/Camera';
import { FiscalAuthorization } from '../../components/FiscalAuthorization';
import { Loading } from '../../components/Loading';
import { NoData } from '../../components/NoData';
import { Question } from '../../components/Question';
import { Requirements } from '../../components/Requirements';
import usePrintScreenDetection from '../../hooks/usePrintScreenDetection';
import {
  buildEnvironmentCheckPayload,
  useSendEnvironmentCheckLogMutation,
} from '../../modules/environment-checker/environmentCheckApiSlice';
import { EnvironmentCheckReport } from '../../modules/environment-checker/types';
import {
  buildIdentityVerificationPayload,
  useSendIdentityVerificationLogMutation,
} from '../../modules/identity-verification/identityVerificationApiSlice';
import type {
  PendingIdentityVerification,
  VerificationResult,
} from '../../modules/identity-verification/types';
import { IdentityVerificationScreen } from '../../modules/identity-verification/ui/IdentityVerificationScreen';
import { LivenessEngine } from '../../modules/liveness-challenge';
import type {
  ChallengeConfig,
  ChallengeStatus,
  ChallengeType,
} from '../../modules/liveness-challenge/types';
import { ChallengeOverlay } from '../../modules/liveness-challenge/ui/ChallengeOverlay';
import {
  Certification,
  CertificationType,
} from '../../types/certifications/Certification';
import { validateUserProfile } from '../../utils/validateUserProfile';
import { useFetchCurrentUserQuery } from '../auth/authApiSlice';
import { selectUser, setCredentials } from '../auth/authSlice';
import {
  useGetCertificationByIdMutation,
  useLoadEnrollQuery,
} from './certificationApiSlice';
import { resolveMonitoringRuntimeConfig } from './config/monitoringConfig';
import { clearGuardState, selectExamGuard, setGuardState } from './examSlice';
import { isScreenshotShortcut } from './guard/screenshotShortcut';
import {
  clearGuardState as clearPersistedGuardState,
  loadGuardState,
  saveGuardState,
} from './guard/storage';
import {
  ExamFlowPhase,
  ExamGuardState,
  MediaValidationStatus,
  ValidationIncident,
} from './guard/types';
import { useExamSecuritySupervisor } from './guard/useExamSecuritySupervisor';
import { useViolationLogger } from './guard/useViolationLogger';
import {
  applyEnvironmentApproval,
  areGuardStatesEqual,
  buildValidationSummary,
  createEmptyCriticalFailureCounts,
  createInitialGuardState,
  createValidationExpirationDate,
  deriveGuardState,
  ensureCriticalFailureCounts,
  getPendingExamPhase,
  getResolvedExamPhase,
  normalizeExamPhase,
} from './guard/utils';
import { generateEvidenceRef } from './liveness/evidenceRef';
import { useLivenessClipRecorder } from './liveness/useLivenessClipRecorder';
import { useScreenShareMonitor } from './screen-share/useScreenShareMonitor';
import { EmailConfirmationGate } from './ui/EmailConfirmationGate';
import { PreparationScreen } from './ui/PreparationScreen';

function requiresCameraMonitoring(type?: CertificationType | null) {
  return type === 'video' || type === 'video_liveness';
}

function getPreparationFlowPhases(
  checkIdentity: boolean,
  monitoringType: CertificationType | null,
): ExamFlowPhase[] {
  const phases: ExamFlowPhase[] = [];

  if (checkIdentity) {
    phases.push('identity_verification');
  }

  phases.push('requirements', 'environment');

  if (monitoringType === 'fiscal') {
    phases.push('monitoring');
  }

  return phases;
}

function createStepLabelMap(phases: ExamFlowPhase[]) {
  return new Map<ExamFlowPhase, string>(
    phases.map((phase, index): [ExamFlowPhase, string] => [
      phase,
      `Etapa ${index + 1} de ${phases.length}`,
    ]),
  );
}

type PreparationApprovalState = {
  mediaPermission: MediaValidationStatus;
  monitoringReady: boolean;
  monitoringType: CertificationType | null;
};

function parsePositiveMinutes(value: unknown) {
  if (value === null || value === undefined || value === '') return undefined;

  const parsedValue =
    typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);

  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return undefined;
  }

  return parsedValue;
}

function buildLivenessConfig(
  certification?: Certification | null,
  livenessChallengeTimeoutSeconds?: number,
): Partial<ChallengeConfig> | undefined {
  if (!certification && livenessChallengeTimeoutSeconds === undefined) {
    return undefined;
  }

  const firstChallengeMinutes = parsePositiveMinutes(
    certification?.liveness_first_challenge_time,
  );
  const challengeIntervalMinutes = parsePositiveMinutes(
    certification?.liveness_challenge_interval,
  );
  const config: Partial<ChallengeConfig> = {};

  if (firstChallengeMinutes !== undefined) {
    config.firstChallengeMinMinutes = firstChallengeMinutes;
    config.firstChallengeMaxMinutes = firstChallengeMinutes;
  }

  if (challengeIntervalMinutes !== undefined) {
    config.minIntervalMinutes = challengeIntervalMinutes;
    config.maxIntervalMinutes = challengeIntervalMinutes;
  }

  if (livenessChallengeTimeoutSeconds !== undefined) {
    config.timeoutSeconds = livenessChallengeTimeoutSeconds;
  }

  return Object.keys(config).length > 0 ? config : undefined;
}

function getLivenessChallengeCopy(challengeType?: ChallengeType | null) {
  switch (challengeType) {
    case 'head_turn_left':
      return {
        title: 'Olhar para a esquerda',
        detail:
          'Virar a cabeça para a esquerda mantendo o rosto no enquadramento.',
      };
    case 'head_turn_right':
      return {
        title: 'Olhar para a direita',
        detail:
          'Virar a cabeça para a direita mantendo o rosto no enquadramento.',
      };
    case 'pitch_up':
      return {
        title: 'Olhar para cima',
        detail: 'Inclinar a cabeça para cima conforme instrução do desafio.',
      };
    case 'pitch_down':
      return {
        title: 'Olhar para baixo',
        detail: 'Inclinar a cabeça para baixo conforme instrução do desafio.',
      };
    case 'blink':
      return {
        title: 'Piscar os olhos',
        detail: 'Realizar as piscadas solicitadas pelo desafio.',
      };
    case 'mouth_open':
      return {
        title: 'Abrir a boca',
        detail: 'Abrir a boca conforme instrução do desafio.',
      };
    default:
      return {
        title: 'Desafio de liveness não identificado',
        detail: 'Concluir a ação de liveness solicitada na tela.',
      };
  }
}

function applyIncidentCount(
  currentGuard: ExamGuardState,
  incident: ValidationIncident,
) {
  const criticalFailureCounts = ensureCriticalFailureCounts(
    currentGuard.criticalFailureCounts,
  );

  return {
    ...currentGuard,
    criticalFailureCounts: {
      ...criticalFailureCounts,
      [incident.reason]: Math.max(
        criticalFailureCounts[incident.reason] ?? 0,
        incident.attempt,
      ),
    },
  };
}

function wasReleasedAfterFailedIdentityAttempts(result: VerificationResult) {
  return (
    result.status === 'passed' &&
    result.releasedByAttemptLimit === true &&
    result.attempts.length > 0 &&
    result.attempts.every(attempt => attempt.status === 'failed')
  );
}

function resetHydratedInExamState(
  guard: ExamGuardState,
  monitoringType?: CertificationType | null,
  focusLossLimit?: number,
) {
  const resolvedFocusLossLimit = focusLossLimit ?? guard.focusLossLimit;

  return deriveGuardState({
    ...guard,
    monitoringType:
      monitoringType !== undefined
        ? monitoringType
        : guard.monitoringType ?? null,
    identityVerified: false,
    requirementsAccepted: false,
    environmentCheck: 'idle',
    mediaPermission: 'idle',
    monitoringReady: false,
    openIncident: null,
    incidents: [],
    criticalFailureCounts: createEmptyCriticalFailureCounts(),
    inExamValidationStatus: 'idle',
    focusLossCount: 0,
    focusLossLimit: resolvedFocusLossLimit,
    remainingFocusAllowance: resolvedFocusLossLimit,
    lastValidatedAt: undefined,
    validationExpiresAt: createValidationExpirationDate(),
  });
}

export function Exam() {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useSelector(selectUser);
  const token = useSelector((state: RootState) => state.auth.token);
  const { data: freshUser } = useFetchCurrentUserQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!freshUser || !token) return;

    dispatch(
      setCredentials({
        token,
        user: {
          _id: freshUser._id,
          email: freshUser.email,
          name: freshUser.name,
          image: freshUser.image,
          extra: freshUser.extra,
          apiKey: user?.apiKey,
        },
      }),
    );
  }, [freshUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const [fiscalApproved, setFiscalApproved] = useState(false);
  const [type, setType] = useState<CertificationType | null>(null);
  const [sendEnvironmentCheckLog] = useSendEnvironmentCheckLogMutation();
  const [sendIdentityVerificationLog] =
    useSendIdentityVerificationLogMutation();
  const pendingIdentityVerificationRef =
    useRef<PendingIdentityVerification | null>(null);
  const [forceTerminationIncident, setForceTerminationIncident] =
    useState<ValidationIncident | null>(null);
  const [focusLossNotice, setFocusLossNotice] =
    useState<ValidationIncident | null>(null);

  const engineRef = useRef<LivenessEngine | null>(null);
  const guardRef = useRef<ExamGuardState | null>(null);
  const hasHydratedGuard = useRef<string | null>(null);
  const cameraControlRef = useRef<CameraControlHandle | null>(null);
  const [examStream, setExamStream] = useState<MediaStream | null>(null);
  const [screenShareStream, setScreenShareStream] =
    useState<MediaStream | null>(null);
  const [cameraRefreshKey, setCameraRefreshKey] = useState(0);
  const pendingCameraRefreshRef = useRef<{
    requestId: number;
    resolve: () => void;
    timeoutId: number;
  } | null>(null);
  const nextCameraRefreshRequestIdRef = useRef(0);
  const [activeChallengeType, setActiveChallengeType] = useState<
    ChallengeType | undefined
  >();
  const activeChallengeTypeRef = useRef<ChallengeType | undefined>();
  const [challengeProgress, setChallengeProgress] = useState(0);
  const [challengeStatus, setChallengeStatus] = useState<
    ChallengeStatus | undefined
  >();
  const [isWarmUp, setIsWarmUp] = useState(false);

  const certificationLogId = params?.certificationId || '';
  const guard = useSelector((state: RootState) =>
    selectExamGuard(state, certificationLogId),
  );

  const [getCertification, { data: certificationItem, isLoading }] =
    useGetCertificationByIdMutation();

  const { data, isFetching } = useLoadEnrollQuery(
    { _id: certificationLogId },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    },
  );
  const { logViolation } = useViolationLogger(
    data?.[0]?._id ?? certificationLogId,
  );

  const resolvedMonitoringType = useMemo<CertificationType | null>(() => {
    if (type) return type;
    if (!certificationItem?.[0]?.monitoring) return null;
    if (certificationItem[0].monitoring === 'select') return null;

    return certificationItem[0].monitoring;
  }, [certificationItem, type]);
  const handleLivenessStateChange = useCallback((event: unknown) => {
    // eslint-disable-next-line no-console
    console.log('[Exam][liveness][state]', event);
  }, []);
  const handleLivenessTerminalState = useCallback((event: unknown) => {
    // eslint-disable-next-line no-console
    console.log('[Exam][liveness][terminal]', event);
  }, []);
  const handleLivenessDiagnostic = useCallback((event: unknown) => {
    // eslint-disable-next-line no-console
    console.log('[Exam][liveness][diagnostic]', event);
  }, []);
  const {
    startChallengeClip,
    finishChallengeClip,
    flushQueue: flushLivenessClipQueue,
  } = useLivenessClipRecorder({
    stream: resolvedMonitoringType === 'video_liveness' ? examStream : null,
    certificationLogId: data?.[0]?._id ?? certificationLogId,
    candidateId: user?._id,
    onStateChange: handleLivenessStateChange,
    onTerminalState: handleLivenessTerminalState,
    onDiagnostic: handleLivenessDiagnostic,
  });

  const certificationData = certificationItem?.[0] ?? null;
  const monitoringRuntimeConfig = useMemo(
    () => resolveMonitoringRuntimeConfig(certificationData),
    [certificationData],
  );
  const {
    environmentCheckTimeoutMs,
    livenessChallengeTimeoutSeconds,
    monitoringIncidentCorrectionWindowSeconds,
    monitoringFocusLossLimit,
    monitoringQuickCheckIntervalSeconds,
    monitoringCheckSecondScreenEnabled,
    monitoringCheckVirtualWebcamEnabled,
    monitoringCheckSuspiciousExtensionsEnabled,
    screenShareEnabled,
    screenShareCaptureIntervalSeconds,
    screenShareIncidentCorrectionWindowSeconds,
    screenShareCaptureQuality,
  } = monitoringRuntimeConfig;

  const supervisorCheckPolicy = useMemo(
    () => ({
      secondScreenEnabled: monitoringCheckSecondScreenEnabled,
      virtualWebcamEnabled: monitoringCheckVirtualWebcamEnabled,
      suspiciousExtensionsEnabled: monitoringCheckSuspiciousExtensionsEnabled,
      screenShareEnabled,
    }),
    [
      monitoringCheckSecondScreenEnabled,
      monitoringCheckVirtualWebcamEnabled,
      monitoringCheckSuspiciousExtensionsEnabled,
      screenShareEnabled,
    ],
  );
  const checkIdentity = certificationData?.check_identity ?? false;
  const preparationFlowPhases = useMemo(
    () => getPreparationFlowPhases(checkIdentity, resolvedMonitoringType),
    [checkIdentity, resolvedMonitoringType],
  );
  const stepLabels = useMemo(
    () => createStepLabelMap(preparationFlowPhases),
    [preparationFlowPhases],
  );

  const livenessConfig = useMemo(
    () =>
      buildLivenessConfig(certificationData, livenessChallengeTimeoutSeconds),
    [certificationData, livenessChallengeTimeoutSeconds],
  );

  const commitGuard = useCallback(
    (nextState: ExamGuardState) => {
      if (areGuardStatesEqual(guardRef.current, nextState)) return;

      guardRef.current = nextState;
      dispatch(setGuardState(nextState));
      saveGuardState(nextState);
    },
    [dispatch],
  );

  const updateGuard = useCallback(
    (updater: (currentGuard: ExamGuardState) => ExamGuardState) => {
      if (!certificationLogId) return;

      const currentGuard =
        guardRef.current ??
        createInitialGuardState(
          certificationLogId,
          resolvedMonitoringType,
          checkIdentity,
          monitoringFocusLossLimit,
        );

      const nextState = deriveGuardState(
        updater({
          ...currentGuard,
          monitoringType: resolvedMonitoringType ?? null,
          checkIdentity,
          focusLossLimit: monitoringFocusLossLimit,
        }),
      );

      commitGuard(nextState);
    },
    [
      certificationLogId,
      checkIdentity,
      commitGuard,
      monitoringFocusLossLimit,
      resolvedMonitoringType,
    ],
  );

  useEffect(() => {
    guardRef.current = guard ?? null;
  }, [guard]);

  useEffect(() => {
    if (data?.[0].certification) {
      getCertification(data[0].certification);
    }
  }, [data, getCertification]);

  useEffect(() => {
    const isProfileComplete = validateUserProfile(user);

    if (!isProfileComplete) {
      navigate('/');
    }
  }, [navigate, user]);

  useEffect(() => {
    if (!certificationLogId) return;

    if (hasHydratedGuard.current !== certificationLogId) {
      const persistedGuard = loadGuardState(certificationLogId);
      const nextGuard = persistedGuard
        ? resetHydratedInExamState(
            { ...persistedGuard, checkIdentity },
            resolvedMonitoringType,
            monitoringFocusLossLimit,
          )
        : createInitialGuardState(
            certificationLogId,
            resolvedMonitoringType,
            checkIdentity,
            monitoringFocusLossLimit,
          );

      commitGuard(nextGuard);
      hasHydratedGuard.current = certificationLogId;
      return;
    }

    const monitoringChanged =
      guard?.monitoringType !== (resolvedMonitoringType ?? null);
    const checkIdentityChanged = guard?.checkIdentity !== checkIdentity;
    const focusLimitChanged =
      guard?.focusLossLimit !== monitoringFocusLossLimit;

    if (
      guard &&
      (monitoringChanged || checkIdentityChanged || focusLimitChanged)
    ) {
      commitGuard(
        deriveGuardState({
          ...guard,
          monitoringType: resolvedMonitoringType ?? null,
          checkIdentity,
          focusLossLimit: monitoringFocusLossLimit,
        }),
      );
    }
  }, [
    certificationLogId,
    checkIdentity,
    commitGuard,
    guard,
    monitoringFocusLossLimit,
    resolvedMonitoringType,
  ]);

  const pendingPhase = guard ? getPendingExamPhase(guard) : 'requirements';
  const requestedPhase = normalizeExamPhase(searchParams.get('phase'));
  const currentPhase = getResolvedExamPhase(requestedPhase, pendingPhase);

  useEffect(() => {
    if (requestedPhase !== currentPhase) {
      setSearchParams({ phase: currentPhase }, { replace: true });
    }
  }, [currentPhase, requestedPhase, setSearchParams]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPhase]);

  const screenShareMonitor = useScreenShareMonitor({
    stream: screenShareStream,
    enabled: currentPhase === 'exam' && screenShareEnabled,
    captureIntervalSeconds: screenShareCaptureIntervalSeconds,
    captureQuality: screenShareCaptureQuality,
    certificationLogId: data?.[0]?._id ?? certificationLogId,
    candidateId: user?._id,
  });
  const { requestNewStream: requestNewScreenShareStream } = screenShareMonitor;

  const handlePhaseNavigation = useCallback(
    (phase: ExamFlowPhase) => {
      setSearchParams({ phase }, { replace: true });
    },
    [setSearchParams],
  );

  const handlePreparationApproved = useCallback(
    async (
      report: EnvironmentCheckReport,
      preparationState: PreparationApprovalState,
    ) => {
      try {
        if (report.candidateId) {
          await sendEnvironmentCheckLog(buildEnvironmentCheckPayload(report));
        }
      } catch {
        // noop
      }

      updateGuard(currentGuard => {
        const approvedGuard = applyEnvironmentApproval(
          {
            ...currentGuard,
            monitoringType:
              preparationState.monitoringType ??
              currentGuard.monitoringType ??
              null,
          },
          report,
        );

        return {
          ...approvedGuard,
          mediaPermission: preparationState.mediaPermission,
          monitoringReady: preparationState.monitoringReady,
          validationExpiresAt: createValidationExpirationDate(),
        };
      });
      handlePhaseNavigation(
        preparationState.monitoringType === 'fiscal' ? 'monitoring' : 'exam',
      );
    },
    [handlePhaseNavigation, sendEnvironmentCheckLog, updateGuard],
  );

  const handleRequirementsAccepted = useCallback(() => {
    updateGuard(currentGuard => ({
      ...currentGuard,
      requirementsAccepted: true,
      validationExpiresAt: createValidationExpirationDate(),
    }));
    handlePhaseNavigation('environment');
  }, [handlePhaseNavigation, updateGuard]);

  const handleIdentityVerified = useCallback(
    async (result: VerificationResult) => {
      try {
        await sendIdentityVerificationLog(
          buildIdentityVerificationPayload(result),
        );
      } catch {
        // noop
      }

      if (result.status !== 'passed') {
        return;
      }

      const verificationStatus: 'verified' | 'failed' =
        wasReleasedAfterFailedIdentityAttempts(result) ? 'failed' : 'verified';

      pendingIdentityVerificationRef.current = {
        status: verificationStatus,
        verificationTime: result.completedAt,
        imageDataUrl: result.capturedImageDataUrl ?? null,
        candidateId: result.candidateId,
        certificationLogId: result.certificationLogId,
      };

      updateGuard(currentGuard => ({
        ...currentGuard,
        identityVerified: true,
        validationExpiresAt: createValidationExpirationDate(),
      }));
      handlePhaseNavigation('requirements');
    },
    [handlePhaseNavigation, sendIdentityVerificationLog, updateGuard],
  );

  useEffect(() => {
    if (!fiscalApproved) return;

    updateGuard(currentGuard => ({
      ...currentGuard,
      monitoringReady: true,
      validationExpiresAt: createValidationExpirationDate(),
    }));
    handlePhaseNavigation('exam');
  }, [fiscalApproved, handlePhaseNavigation, updateGuard]);

  const dismissFocusLossNotice = useCallback(() => {
    setFocusLossNotice(null);
  }, []);

  const resolvePendingCameraRefresh = useCallback(() => {
    const pendingRefresh = pendingCameraRefreshRef.current;
    if (!pendingRefresh) return;

    pendingCameraRefreshRef.current = null;
    window.clearTimeout(pendingRefresh.timeoutId);
    pendingRefresh.resolve();
  }, []);

  const refreshExamCameraBeforeRetry = useCallback(
    async ({ source }: { source?: 'manual' | 'deadline' }) => {
      const currentIncidentReason = guardRef.current?.openIncident?.reason;

      if (currentIncidentReason === 'screen_share_stopped') {
        const newStream = await requestNewScreenShareStream();
        setScreenShareStream(newStream);
        return;
      }

      const requiresCameraRefresh =
        currentIncidentReason === 'camera_stream_lost' ||
        currentIncidentReason === 'virtual_camera_in_use';

      if (
        !requiresCameraMonitoring(resolvedMonitoringType) ||
        !requiresCameraRefresh
      ) {
        return;
      }

      resolvePendingCameraRefresh();

      if (engineRef.current) {
        engineRef.current.stop();
        engineRef.current = null;
      }

      cameraControlRef.current?.endLivenessChallenge(
        activeChallengeTypeRef.current,
      );
      activeChallengeTypeRef.current = undefined;
      setActiveChallengeType(undefined);
      setChallengeStatus(undefined);
      setChallengeProgress(0);
      setIsWarmUp(false);
      setExamStream(null);

      const requestId = nextCameraRefreshRequestIdRef.current + 1;
      nextCameraRefreshRequestIdRef.current = requestId;

      // eslint-disable-next-line no-console
      console.log('[Exam][monitoring][refresh_camera_before_revalidation]', {
        source: source ?? 'manual',
        requestId,
        reason: currentIncidentReason,
      });

      await new Promise<void>(resolve => {
        const timeoutId = window.setTimeout(() => {
          if (pendingCameraRefreshRef.current?.requestId !== requestId) return;

          pendingCameraRefreshRef.current = null;
          resolve();
        }, environmentCheckTimeoutMs);

        pendingCameraRefreshRef.current = {
          requestId,
          resolve,
          timeoutId,
        };

        setCameraRefreshKey(currentKey => currentKey + 1);
      });
    },
    [
      environmentCheckTimeoutMs,
      resolvePendingCameraRefresh,
      resolvedMonitoringType,
      requestNewScreenShareStream,
    ],
  );

  useEffect(() => {
    if (!focusLossNotice) return undefined;

    const timeoutId = window.setTimeout(() => {
      setFocusLossNotice(currentNotice =>
        currentNotice?.timestamp === focusLossNotice.timestamp
          ? null
          : currentNotice,
      );
    }, 7000);

    return () => window.clearTimeout(timeoutId);
  }, [focusLossNotice]);

  useEffect(() => {
    return () => {
      resolvePendingCameraRefresh();
    };
  }, [resolvePendingCameraRefresh]);

  const { countdown, reportMonitoringFailure, retryValidation, statusLabel } =
    useExamSecuritySupervisor({
      certificationLogId,
      enabled: currentPhase === 'exam',
      monitoringType: resolvedMonitoringType,
      stream: examStream,
      focusLossLimit: monitoringFocusLossLimit,
      incidentCorrectionWindowSeconds:
        monitoringIncidentCorrectionWindowSeconds,
      screenShareIncidentCorrectionWindowSeconds,
      quickCheckIntervalSeconds: monitoringQuickCheckIntervalSeconds,
      checkPolicy: supervisorCheckPolicy,
      initialCriticalFailureCounts: ensureCriticalFailureCounts(
        guard?.criticalFailureCounts,
      ),
      screenShareActive: screenShareMonitor.isActive,
      prepareRetryValidation: refreshExamCameraBeforeRetry,
      onFocusLossNotice: incident => {
        if (incident.reason === 'browser_focus_lost') {
          logViolation({
            rule: 'perdeu o foco do navegador',
            source: 'focus_lost',
            severity: 'high',
            title: 'Perda de foco detectada',
            description: 'Perdeu o foco do navegador.',
            occurredAt: incident.timestamp,
            context: {
              phase: 'exam',
              check_id: incident.checkId,
              occurrence: incident.occurrence,
              monitoring_type: resolvedMonitoringType ?? 'none',
              document_hidden: document.hidden,
              document_focused: document.hasFocus(),
            },
          });
        }

        setFocusLossNotice(incident);
        updateGuard(currentGuard => ({
          ...applyIncidentCount(currentGuard, incident),
          incidents: currentGuard.incidents.some(
            existingIncident =>
              existingIncident.timestamp === incident.timestamp,
          )
            ? currentGuard.incidents.map(existingIncident =>
                existingIncident.timestamp === incident.timestamp
                  ? incident
                  : existingIncident,
              )
            : [...currentGuard.incidents, incident],
          inExamValidationStatus:
            currentGuard.inExamValidationStatus === 'terminated'
              ? 'terminated'
              : 'warning',
          lastValidatedAt: incident.timestamp,
          validationExpiresAt: createValidationExpirationDate(),
        }));
      },
      onIncidentOpen: incident => {
        if (incident.reason === 'extra_monitor') {
          logViolation({
            rule: 'monitor externo detectado',
            source: 'external_monitor',
            severity: 'high',
            title: 'Monitor externo detectado',
            description:
              'Foi detectado mais de um monitor conectado durante a prova.',
            occurredAt: incident.timestamp,
            context: {
              phase: 'exam',
              check_id: incident.checkId,
              occurrence: incident.occurrence,
              monitoring_type: resolvedMonitoringType ?? 'none',
            },
          });
        }

        if (incident.reason === 'virtual_camera_in_use') {
          logViolation({
            rule: 'webcam virtual detectada',
            source: 'virtual_webcam',
            severity: 'high',
            title: 'Webcam virtual detectada',
            description:
              'Foi detectado uso de cÃ¢mera virtual durante a prova.',
            occurredAt: incident.timestamp,
            context: {
              phase: 'exam',
              check_id: incident.checkId,
              occurrence: incident.occurrence,
              monitoring_type: resolvedMonitoringType ?? 'none',
            },
          });
        }

        if (incident.reason === 'screen_share_stopped') {
          logViolation({
            rule: 'compartilhamento de tela interrompido',
            source: 'screen_share_stopped',
            severity: 'high',
            title: 'Compartilhamento de tela interrompido',
            description:
              'O compartilhamento de tela foi interrompido durante a prova.',
            occurredAt: incident.timestamp,
            context: {
              phase: 'exam',
              check_id: incident.checkId,
              occurrence: incident.occurrence,
              monitoring_type: resolvedMonitoringType ?? 'none',
            },
          });
        }

        updateGuard(currentGuard => ({
          ...applyIncidentCount(currentGuard, incident),
          openIncident: incident,
          incidents: [...currentGuard.incidents, incident],
          inExamValidationStatus: 'critical',
          validationExpiresAt: createValidationExpirationDate(),
        }));
      },
      onIncidentResolved: incident => {
        updateGuard(currentGuard => ({
          ...currentGuard,
          openIncident: null,
          incidents: currentGuard.incidents.map(existingIncident =>
            existingIncident.timestamp === incident.timestamp
              ? incident
              : existingIncident,
          ),
          inExamValidationStatus: 'approved',
          lastValidatedAt: incident.resolvedAt,
          validationExpiresAt: createValidationExpirationDate(),
        }));
      },
      onTerminateExam: incident => {
        setForceTerminationIncident(incident);
        updateGuard(currentGuard => ({
          ...applyIncidentCount(currentGuard, incident),
          openIncident: incident,
          incidents: currentGuard.incidents.some(
            existingIncident =>
              existingIncident.timestamp === incident.timestamp,
          )
            ? currentGuard.incidents.map(existingIncident =>
                existingIncident.timestamp === incident.timestamp
                  ? incident
                  : existingIncident,
              )
            : [...currentGuard.incidents, incident],
          inExamValidationStatus: 'terminated',
          validationExpiresAt: createValidationExpirationDate(),
        }));
      },
      onStatusChange: status => {
        updateGuard(currentGuard => ({
          ...currentGuard,
          inExamValidationStatus: status,
          lastValidatedAt: new Date().toISOString(),
          validationExpiresAt: createValidationExpirationDate(),
        }));
      },
    });

  const handlePrintscreenClipboard = useCallback(() => {
    if (currentPhase !== 'exam') return;

    logViolation({
      rule: 'tirou print screen da tela',
      source: 'printscreen_clipboard',
      severity: 'medium',
      title: 'Tentativa de captura de tela',
      description:
        'Foi detectada imagem no clipboard durante a prova (possÃ­vel print screen).',
      context: {
        phase: 'exam',
        clipboard_image_detected: true,
        monitoring_type: resolvedMonitoringType ?? 'none',
      },
    });
  }, [currentPhase, logViolation, resolvedMonitoringType]);

  usePrintScreenDetection(handlePrintscreenClipboard);

  useEffect(() => {
    if (currentPhase !== 'exam') return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      const result = isScreenshotShortcut(event);
      if (!result.matched) return;

      logViolation({
        rule: 'tirou print screen da tela',
        source: 'printscreen_hotkey',
        severity: 'medium',
        title: 'Tentativa de captura de tela',
        description:
          'Foi detectado uso de atalho de print screen durante a prova.',
        context: {
          phase: 'exam',
          key_combo: result.combo,
          monitoring_type: resolvedMonitoringType ?? 'none',
        },
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPhase, logViolation, resolvedMonitoringType]);

  const handleStreamReady = useCallback(
    (stream: MediaStream) => {
      setExamStream(stream);
      resolvePendingCameraRefresh();

      if (requiresCameraMonitoring(resolvedMonitoringType)) {
        updateGuard(currentGuard => ({
          ...currentGuard,
          mediaPermission: 'preview_ready',
          monitoringReady: true,
          validationExpiresAt: createValidationExpirationDate(),
        }));
      }
    },
    [resolvePendingCameraRefresh, resolvedMonitoringType, updateGuard],
  );

  useEffect(() => {
    const shouldInitLiveness =
      resolvedMonitoringType === 'video_liveness' &&
      !engineRef.current &&
      currentPhase === 'exam' &&
      examStream;

    if (!shouldInitLiveness || !examStream) return;

    const engine = new LivenessEngine();

    engine.onChallengeStart = (challengeType: ChallengeType) => {
      cameraControlRef.current?.beginLivenessChallenge(challengeType);
      activeChallengeTypeRef.current = challengeType;
      setActiveChallengeType(challengeType);
      setChallengeStatus(undefined);
      setChallengeProgress(0);
      setIsWarmUp(true);
      const sessionRef = startChallengeClip({
        challengeType,
        scheduledAtIso: new Date().toISOString(),
      });
      // eslint-disable-next-line no-console
      console.log('[Exam][liveness][challenge_start]', {
        challengeType,
        sessionRef,
      });
    };

    engine.onProgress = (progress: number) => {
      setChallengeProgress(progress);
    };

    engine.onWarmUpEnd = () => {
      setIsWarmUp(false);
    };

    engine.onChallengeEnd = result => {
      setChallengeStatus(result.status);
      setChallengeProgress(result.confidence ?? 0);
      setIsWarmUp(false);

      const challengeType =
        result.type ?? activeChallengeTypeRef.current ?? 'blink';
      const evidenceRef =
        result.status === 'failed' || result.status === 'timeout'
          ? generateEvidenceRef()
          : null;
      // eslint-disable-next-line no-console
      console.log('[Exam][liveness][challenge_end]', {
        challengeId: result.challengeId,
        challengeType,
        status: result.status,
        confidence: result.confidence ?? null,
        evidenceRef,
      });

      if (result.status === 'failed' || result.status === 'timeout') {
        const challengeCopy = getLivenessChallengeCopy(challengeType);
        const isTimeout = result.status === 'timeout';

        logViolation({
          rule: 'desafio de liveness nao concluido',
          source: isTimeout ? 'liveness_timeout' : 'liveness_failed',
          severity: 'high',
          title: 'Liveness nÃ£o concluÃ­do',
          description: isTimeout
            ? `NÃ£o concluiu o desafio de liveness: ${challengeCopy.title}.`
            : `Falhou no desafio de liveness: ${challengeCopy.title}.`,
          occurredAt: new Date().toISOString(),
          context: {
            phase: 'exam',
            challenge_type: challengeType,
            challenge_name: challengeCopy.title,
            challenge_expected_action: challengeCopy.detail,
            challenge_status: result.status,
            timeout_reason: isTimeout
              ? `Não concluiu: ${challengeCopy.title}`
              : null,
            liveness_confidence: result.confidence ?? null,
            evidence_ref: evidenceRef,
            monitoring_type: resolvedMonitoringType ?? 'none',
          },
        });
        reportMonitoringFailure(
          'Desafio de verificação de identidade não concluído. Fique atento ao próximo.',
        );
      }

      finishChallengeClip({
        challengeId: result.challengeId,
        challengeType,
        challengeStatus: result.status,
        startedAtIso: result.startedAt,
        completedAtIso: result.completedAt,
        confidence: result.confidence,
        evidenceRef,
      }).catch(error => {
        // eslint-disable-next-line no-console
        console.error('[Exam][liveness][finish_error]', {
          challengeId: result.challengeId,
          challengeType,
          status: result.status,
          error: error instanceof Error ? error.message : String(error),
        });
      });

      setTimeout(() => {
        cameraControlRef.current?.endLivenessChallenge(challengeType);
        activeChallengeTypeRef.current = undefined;
        setActiveChallengeType(undefined);
        setChallengeStatus(undefined);
      }, 2500);
    };

    engine.onBlocked = () => {
      logViolation({
        rule: 'falha critica de liveness detectada',
        source: 'liveness_blocked',
        severity: 'high',
        title: 'Falha críticas de liveness',
        description:
          'O monitoramento detectou falha críticas de liveness e requer revalidação imediata.',
        occurredAt: new Date().toISOString(),
        context: {
          phase: 'exam',
          challenge_status: 'blocked',
          monitoring_type: resolvedMonitoringType ?? 'none',
        },
      });
      reportMonitoringFailure(
        'Falha críticas de monitoramento/liveness detectada. Reposicione-se diante da câmera e revalide imediatamente.',
      );
    };

    engine.start(
      examStream,
      user?._id ?? '',
      data?.[0]?._id ?? '',
      livenessConfig,
    );
    engineRef.current = engine;
  }, [
    currentPhase,
    data,
    examStream,
    finishChallengeClip,
    logViolation,
    livenessConfig,
    reportMonitoringFailure,
    resolvedMonitoringType,
    startChallengeClip,
    user,
  ]);

  const handleExamFinalized = useCallback(() => {
    if (engineRef.current) {
      engineRef.current.stop();
      engineRef.current = null;
    }
    cameraControlRef.current?.endLivenessChallenge(
      activeChallengeTypeRef.current,
    );

    flushLivenessClipQueue(3000).catch(() => undefined);

    screenShareStream?.getTracks().forEach(track => track.stop());
    setScreenShareStream(null);

    if (certificationLogId) {
      clearPersistedGuardState(certificationLogId);
      dispatch(clearGuardState(certificationLogId));
    }
  }, [certificationLogId, dispatch, flushLivenessClipQueue, screenShareStream]);

  useEffect(() => {
    const cameraControl = cameraControlRef.current;

    return () => {
      if (engineRef.current) {
        engineRef.current.stop();
        engineRef.current = null;
      }
      cameraControl?.endLivenessChallenge(activeChallengeTypeRef.current);

      flushLivenessClipQueue(3000).catch(() => undefined);
    };
  }, [flushLivenessClipQueue]);

  if (isLoading) return <Loading />;
  if (!certificationItem?.[0]) return <NoData title="Sem dados" />;
  if (!guard) return <Loading />;

  // Email confirmation gate — block exam if emailConfirmed === false
  const emailConfirmed = (freshUser ?? user)?.extra?.emailConfirmed;
  if (emailConfirmed === false) {
    return (
      <EmailConfirmationGate
        playerId={user?._id ?? ''}
        variant="page"
        onConfirmed={() => window.location.reload()}
      />
    );
  }

  if (currentPhase === 'identity_verification') {
    return (
      <IdentityVerificationScreen
        candidateId={user?._id ?? ''}
        certificationLogId={certificationLogId}
        profileImageItem={user?.image?.original ?? null}
        stepLabel={stepLabels.get('identity_verification')}
        minAttempts={certificationData?.check_identity_min_attempts ?? null}
        allowPassOnAttemptLimit={
          certificationData?.check_identity_allow_pass_on_attempt_limit ?? null
        }
        onVerified={handleIdentityVerified}
        onBack={() => navigate('/my-certifications')}
      />
    );
  }

  if (currentPhase === 'environment') {
    return (
      <PreparationScreen
        candidateId={user?._id ?? ''}
        certificationLogId={data?.[0]?._id ?? ''}
        certification={certificationItem[0]}
        selectedMonitoringType={type}
        stepLabel={stepLabels.get('environment')}
        onBack={() => handlePhaseNavigation('requirements')}
        onMonitoringTypeChange={selectedType => {
          setType(selectedType);
          updateGuard(currentGuard => ({
            ...currentGuard,
            monitoringType: selectedType,
            validationExpiresAt: createValidationExpirationDate(),
          }));
        }}
        onApproved={handlePreparationApproved}
        onScreenShareStream={setScreenShareStream}
      />
    );
  }

  if (currentPhase === 'requirements') {
    return (
      <Requirements
        certification={certificationItem[0]}
        stepLabel={stepLabels.get('requirements')}
        onAccept={handleRequirementsAccepted}
        onBack={() => {
          if (checkIdentity) {
            handlePhaseNavigation('identity_verification');
            return;
          }
          navigate('/my-certifications');
        }}
      />
    );
  }

  if (currentPhase === 'monitoring') {
    if (resolvedMonitoringType === 'fiscal') {
      return (
        <FiscalAuthorization
          certificationId={data?.[0].certification || ''}
          certificationLog={data?.[0]?._id || ''}
          stepLabel={stepLabels.get('monitoring')}
          onFiscalAuthorization={setFiscalApproved}
          onBack={() => handlePhaseNavigation('environment')}
        />
      );
    }
  }

  const isTerminatedIncident = guard.inExamValidationStatus === 'terminated';
  const openIncidentLimit = guard.openIncident?.limit;
  let openIncidentOccurrence = 0;
  if (guard.openIncident) {
    openIncidentOccurrence = guard.openIncident.occurrence;
    if (openIncidentLimit) {
      openIncidentOccurrence = Math.min(
        guard.openIncident.occurrence,
        openIncidentLimit,
      );
    }
  }
  const usesUnifiedInfractionPolicy =
    guard.openIncident?.reason === 'browser_focus_lost' ||
    guard.openIncident?.reason === 'monitoring_failure' ||
    guard.openIncident?.reason === 'extra_monitor' ||
    guard.openIncident?.reason === 'screen_share_stopped';
  const terminatedIncidentMessage = usesUnifiedInfractionPolicy
    ? `O limite de ${guard.focusLossLimit} infrações foi atingido. A prova está sendo encerrada e enviada automaticamente. Aguarde a navegação para o resultado.`
    : 'A política de segurança foi acionada. A prova está sendo encerrada e enviada automaticamente. Aguarde a navegação para o resultado.';

  return (
    <div className="min-h-screen bg-slate-100 pb-10">
      {isFetching && (
        <div className="sticky top-0 z-40 bg-amber-50 px-4 py-2 text-center text-sm text-amber-800">
          Reconectando dados do exame...
        </div>
      )}

      {guard.openIncident && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4">
          <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-red-600">
              {guard.openIncident.reasonLabel}
            </p>
            <h2 className="mt-3 text-2xl font-semibold text-slate-900">
              {isTerminatedIncident
                ? 'A prova está sendo encerrada e enviada'
                : 'Interação bloqueada até a correção'}
            </h2>
            <p className="mt-3 text-sm text-slate-600">
              {isTerminatedIncident
                ? terminatedIncidentMessage
                : guard.openIncident.message}
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <strong className="block text-slate-900">Ocorrência</strong>
                {openIncidentOccurrence}
                {openIncidentLimit ? ` de ${openIncidentLimit}` : ''}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <strong className="block text-slate-900">
                  {isTerminatedIncident
                    ? 'Status do encerramento'
                    : 'Restantes antes do encerramento'}
                </strong>
                {isTerminatedIncident
                  ? 'Envio em andamento'
                  : guard.openIncident.remainingAllowance ?? 0}
              </div>
            </div>
            {!isTerminatedIncident && (
              <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Tempo para correção: <strong>{countdown}s</strong>
              </div>
            )}
            {isTerminatedIncident && (
              <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                O envio final acontece automaticamente com as respostas já
                registradas. Não feche a aba até o redirecionamento.
              </div>
            )}
            {!isTerminatedIncident && guard.openIncident.resolutionHint && (
              <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                {guard.openIncident.resolutionHint}
              </div>
            )}
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <strong className="block text-slate-900">Política ativa</strong>
              {usesUnifiedInfractionPolicy
                ? `A prova encerra automaticamente ao atingir ${guard.focusLossLimit} infrações unificadas de foco, liveness, monitor extra ou compartilhamento de tela interrompido.`
                : 'Falhas críticas de câmera e monitoramento continuam com encerramento imediato na reincidência.'}
            </div>
            {!isTerminatedIncident && (
              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => navigate('/my-certifications')}
                  className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                >
                  Sair da prova
                </button>
                <button
                  type="button"
                  onClick={() => {
                    retryValidation().catch(() => undefined);
                  }}
                  className="rounded-xl bg-blue-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-800"
                >
                  Revalidar agora
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {focusLossNotice && (
        <div className="fixed inset-x-2 top-20 z-50 mx-auto max-w-md sm:inset-x-auto sm:right-4 sm:left-auto sm:top-24">
          <div className="rounded-3xl border border-amber-200 bg-white p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-600">
                  {focusLossNotice.reasonLabel}
                </p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                  {focusLossNotice.reason === 'monitoring_failure'
                    ? 'Desafio de liveness não concluído'
                    : 'A prova perdeu o foco'}
                </h2>
              </div>
              <button
                type="button"
                onClick={dismissFocusLossNotice}
                className="rounded-full border border-slate-300 px-3 py-1 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
              >
                Fechar
              </button>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              {focusLossNotice.message}
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <strong className="block text-slate-900">Ocorrência</strong>
                {focusLossNotice.occurrence}
                {focusLossNotice.limit ? ` de ${focusLossNotice.limit}` : ''}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <strong className="block text-slate-900">
                  Restantes até o limite
                </strong>
                {focusLossNotice.remainingAllowance ?? 0}
              </div>
            </div>
            {focusLossNotice.resolutionHint && (
              <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                {focusLossNotice.resolutionHint}
              </div>
            )}
            <p className="mt-4 text-xs text-slate-500">
              Este aviso fecha sozinho se você não interagir.
            </p>
          </div>
        </div>
      )}

      <ChallengeOverlay
        isActive={!!activeChallengeType && currentPhase === 'exam'}
        challengeType={activeChallengeType}
        progress={challengeProgress}
        status={challengeStatus}
        isWarmUp={isWarmUp}
        timeoutSeconds={livenessChallengeTimeoutSeconds}
        stream={examStream}
      />

      <Question
        certification={certificationItem[0]}
        certificationLog={data?.[0]}
        type={resolvedMonitoringType}
        cameraRefreshKey={cameraRefreshKey}
        validationSummary={buildValidationSummary(guard)}
        canFinishExam={guard.canFinishExam}
        monitoringStatusLabel={statusLabel}
        validationStatus={guard.inExamValidationStatus}
        openIncident={guard.openIncident}
        forceTerminationIncident={forceTerminationIncident}
        latestIncident={guard.incidents[guard.incidents.length - 1] ?? null}
        incidents={guard.incidents}
        focusLossCount={guard.focusLossCount}
        focusLossLimit={guard.focusLossLimit}
        remainingFocusAllowance={guard.remainingFocusAllowance}
        isLivenessChallengeActive={Boolean(activeChallengeType)}
        pendingIdentityVerification={pendingIdentityVerificationRef.current}
        cameraControlRef={cameraControlRef}
        onStreamReady={handleStreamReady}
        onExamFinalized={handleExamFinalized}
      />
    </div>
  );
}
